import React, { useState, useEffect } from 'react';
import Navbar from '../Navbar/Navbar';
import './CraneRental.scss';
import api from '../Axios/axios';
import { useLocation, useNavigate } from 'react-router-dom';

const isApprovedRentalStatus = (value) =>
  value === 'Approved' || value === 'approved' || value === 2;

const isActiveTowerCraneStatus = (value) =>
  value === 'Active' || value === 'active' || value === 1;

const OPEN_TECHNICAL_SERVICE_STATUSES = new Set([1, 2, 3, 4, 5]);

const normalizeTechnicalServiceStatus = (status) => {
  if (typeof status === 'number') return status;

  const statusMap = {
    New: 1,
    Assigned: 2,
    InProgress: 3,
    CompletedByPersonnel: 4,
    WaitingManagerApproval: 5,
    Approved: 6,
    SentToAccounting: 7,
    Cancelled: 8
  };

  if (typeof status === 'string') {
    if (statusMap[status]) return statusMap[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const normalizeTowerCraneType = (type) => {
  if (type === 1 || type === '1' || type === 'Chassis') return 'Chassis';
  if (type === 2 || type === '2' || type === 'Anchored') return 'Anchored';
  if (type === 3 || type === '3' || type === 'Walking') return 'Walking';
  if (type === 'Şaseli') return 'Chassis';
  if (type === 'Ankarajlı') return 'Anchored';
  if (type === 'Yürüyüşlü') return 'Walking';
  return 'Chassis';
};

const getTowerCraneTypeLabel = (type) => {
  const normalizedType = normalizeTowerCraneType(type);
  if (normalizedType === 'Anchored') return 'Ankarajlı';
  if (normalizedType === 'Walking') return 'Yürüyüşlü';
  return 'Şaseli';
};

const isManualRentalActive = (crane) => {
  if (!crane?.isManualRented) return false;
  return true;
};

const isManualReservationActive = (crane) => {
  if (!crane?.isManualReserved) return false;
  return true;
};

const createRentalChargeItem = () => ({
  localId: `rental-charge-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  description: '',
  quantity: 1,
  unitPrice: ''
});

const normalizeNumberForApi = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';

  const normalized = text
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? String(number) : '';
};

const getApiNumber = (value) => {
  const normalized = normalizeNumberForApi(value);
  return normalized === '' ? null : Number(normalized);
};

const appendValue = (formData, key, value) => {
  formData.append(key, value == null ? '' : String(value).trim());
};

const appendNumber = (formData, key, value) => {
  formData.append(key, normalizeNumberForApi(value));
};

const CRANE_RENTAL_COMPANIES_CACHE_KEY = 'craneRentalCompaniesCacheV1';
const STAR_CRANE_COMPANY_NAME = 'STAR KULE VİNÇ VE İŞ MAKİNELERİ KİRALAMA ANONİM ŞİRKETİ';

const readCompaniesCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CRANE_RENTAL_COMPANIES_CACHE_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

const writeCompaniesCache = (companies) => {
  try {
    sessionStorage.setItem(CRANE_RENTAL_COMPANIES_CACHE_KEY, JSON.stringify(companies || []));
  } catch (error) {
    // Cache yazılamazsa ekran normal API cevabıyla çalışmaya devam eder.
  }
};

const CraneRental = ({ role }) => {
  const cachedCompanies = readCompaniesCache();
  const [craneOwners, setCraneOwners] = useState(() => cachedCompanies.filter(owner => owner.hasTowerCrane));
  const [allCranes, setAllCranes] = useState([]);
  const [allRentals, setAllRentals] = useState([]);
  const [allReserves, setAllReserves] = useState([]);
  const [technicalServiceRequests, setTechnicalServiceRequests] = useState([]);
  const [selectedCrane, setSelectedCrane] = useState(null);
  const [tenantDetails, setTenantDetails] = useState({
    companyName: '',
    taxOffice: '',
    taxNo: '',
    address: '',
    authorizedPerson: '',
    phone: '',
    email: '',
    workSiteName: '',
  });
  const [rentalDetails, setRentalDetails] = useState({
    rentalCompany: '',
    site: '',
    rentalDuration: '',
    rentalFee: '',
    invoiceInstallmentCount: '',
    firstInvoiceDate: '',
    machineRentCompanyId: '',
    startDate: '',
    endDate: '',
    installationHeight: '',
    JibLength:'',
    type: '',
    hasWalkingGear: '',
    notes: [{ id: 1, note: '' }],
    lineItems: [createRentalChargeItem()],
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmittingRental, setIsSubmittingRental] = useState(false);
  const [files, setFiles] = useState([]);
  const handleTenantDetailChange = (e) => {
    const { name, value } = e.target;
    setTenantDetails((current) => ({ ...current, [name]: value }));
  };
  useEffect(() => {
    const fetchInitialData = async () => {
      api.get('/Company')
        .then((companyResponse) => {
        const companies = companyResponse?.data?.data || [];
          writeCompaniesCache(companies);
        const filteredOwners = companies.filter(owner => owner.hasTowerCrane);

        setCraneOwners(filteredOwners);
        })
        .catch((error) => {
          console.error('Makine kiralayan firmalar yüklenemedi:', error);
        });

      try {
        const [craneResponse, rentalResponse, reserveResponse, technicalServiceResponse] = await Promise.all([
          api.get('/TowerCrane').catch(() => ({ data: { data: [] } })),
          api.get('/Rental', { params: { serviceStatus: 'Approved', includeLineItems: false } }).catch(() => ({ data: { data: [] } })),
          api.get('/Reserve').catch(() => ({ data: { data: [] } })),
          api.get('/TechnicalService/requests').catch(() => ({ data: { data: [] } }))
        ]);

        setAllCranes(craneResponse?.data?.data || []);
        setAllRentals(rentalResponse?.data?.data || []);
        setAllReserves(reserveResponse?.data?.data || []);
        setTechnicalServiceRequests(technicalServiceResponse?.data?.data || []);
      } catch (error) {
        console.error('Vinç kiralama verileri yüklenemedi:', error);
      }
    };

    fetchInitialData();
  }, []);

  const activeRentalIds = React.useMemo(
    () =>
      new Set(
        (allRentals || [])
          .filter((rental) => rental?.isActive && isApprovedRentalStatus(rental?.serviceStatus))
          .map((rental) => rental.towerCraneId)
      ),
    [allRentals]
  );

  const reservedCraneIds = React.useMemo(
    () =>
      new Set(
        (allReserves || [])
          .filter((reserve) => String(reserve?.reserveType || '').toLowerCase() === 'reserved')
          .map((reserve) => reserve.towerCraneId)
      ),
    [allReserves]
  );

  const technicalServiceCraneIds = React.useMemo(
    () =>
      new Set(
        (technicalServiceRequests || [])
          .filter((request) => OPEN_TECHNICAL_SERVICE_STATUSES.has(normalizeTechnicalServiceStatus(request?.status)))
          .map((request) => Number(request?.towerCraneId))
          .filter((towerCraneId) => towerCraneId > 0)
      ),
    [technicalServiceRequests]
  );

  const availableCranes = React.useMemo(
    () =>
      (allCranes || []).filter((crane) => {
        if (!isActiveTowerCraneStatus(crane.status)) return false;
        if (activeRentalIds.has(crane.id) || reservedCraneIds.has(crane.id)) return false;
        if (isManualRentalActive(crane)) return false;
        if (isManualReservationActive(crane)) return false;
        if (crane.hasOpenTechnicalService || technicalServiceCraneIds.has(Number(crane.id))) return false;
        return true;
      }),
    [activeRentalIds, allCranes, reservedCraneIds, technicalServiceCraneIds]
  );
 
  const applySelectedCrane = (selectedCraneData) => {
    setSelectedCrane(selectedCraneData || null);
    setRentalDetails((current) => ({
      ...current,
      chassisType: selectedCraneData ? normalizeTowerCraneType(selectedCraneData.type) : '',
      hasWalkingGear:
        selectedCraneData && normalizeTowerCraneType(selectedCraneData.type) === 'Walking'
          ? 'Var'
          : selectedCraneData?.hasWalkingGear
            ? 'Var'
            : 'Yok',
      installationHeight: selectedCraneData?.height || '',
      JibLength: selectedCraneData?.bom || '',
    }));
  };

  const handleCraneChange = (e) => {
    const selectedCraneData = availableCranes.find(crane => crane.id === parseInt(e.target.value, 10));
    applySelectedCrane(selectedCraneData);
  };

  const preselectedCraneId = React.useMemo(() => {
    const fromState = location.state?.selectedCraneId;
    const fromQuery = new URLSearchParams(location.search).get('craneId');
    const parsed = Number(fromState || fromQuery);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [location.search, location.state]);

  useEffect(() => {
    if (!preselectedCraneId || selectedCrane?.id === preselectedCraneId) return;
    const selectedCraneData = availableCranes.find((crane) => Number(crane.id) === preselectedCraneId);
    if (selectedCraneData) {
      applySelectedCrane(selectedCraneData);
    }
  }, [availableCranes, preselectedCraneId, selectedCrane?.id]);

  const handleRentalDetailChange = (e) => {
    const { name, value } = e.target;
    const nextDetails = {
      ...rentalDetails,
      [name]: value,
    };

    if (name === 'rentalDuration') {
      const currentInstallmentCount = String(rentalDetails.invoiceInstallmentCount || '');
      const currentRentalDuration = String(rentalDetails.rentalDuration || '');
      if (!currentInstallmentCount || currentInstallmentCount === currentRentalDuration) {
        nextDetails.invoiceInstallmentCount = value;
      }
    }

    if (name === 'startDate') {
      const currentFirstInvoiceDate = rentalDetails.firstInvoiceDate || '';
      const currentStartDate = rentalDetails.startDate || '';
      if (!currentFirstInvoiceDate || currentFirstInvoiceDate === currentStartDate) {
        nextDetails.firstInvoiceDate = value;
      }
    }

    setRentalDetails(nextDetails);
    if (name === 'rentalDuration') {
      const rawStartDate = rentalDetails.startDate;
      const rawDuration = value;

      // rentalDuration boşsa veya sayı değilse işlem yapma
      if (!rawDuration || isNaN(parseInt(rawDuration, 10))) return;

      const startDate = new Date(rawStartDate);

      // startDate geçerli değilse işlem yapma
      if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return;

      startDate.setMonth(startDate.getMonth() + parseInt(rawDuration, 10));
      startDate.setDate(startDate.getDate() - 1);

      setRentalDetails((prevState) => ({
        ...prevState,
        endDate: startDate.toISOString().slice(0, 10),
      }));
    }

  };

  const handleFileChange = (e, fileType) => {

    var f = files.find(x => x.fileType === fileType);
    if(f){
      f.file = Array.from(e.target.files) 
    }
    else{
      files.push({
        fileType: fileType, 
        file: Array.from(e.target.files) 
      })
    }
  };

  const handleNoteChange = (index, value) => {
    const updatedNotes = [...rentalDetails.notes];
    updatedNotes[index].note = value;
    setRentalDetails({ ...rentalDetails, notes: updatedNotes });
  };

  const addNote = () => {
    setRentalDetails((prevState) => ({
      ...prevState,
      notes: [...prevState.notes, { id: prevState.notes.length + 1, note: '' }],
    }));
  };

  const handleLineItemChange = (localId, field, value) => {
    setRentalDetails((prevState) => ({
      ...prevState,
      lineItems: (prevState.lineItems || []).map((item) =>
        item.localId === localId ? { ...item, [field]: value } : item
      )
    }));
  };

  const addLineItem = () => {
    setRentalDetails((prevState) => ({
      ...prevState,
      lineItems: [...(prevState.lineItems || []), createRentalChargeItem()]
    }));
  };

  const removeLineItem = (localId) => {
    setRentalDetails((prevState) => ({
      ...prevState,
      lineItems:
        (prevState.lineItems || []).length > 1
          ? prevState.lineItems.filter((item) => item.localId !== localId)
          : prevState.lineItems
    }));
  };
// rentalPeriod değiştiğinde çağrılacak fonksiyon
const calculateRentalEndDate = () => { 
  const { startDate, rentalDuration } = rentalDetails;

  // Geçerli tarih ve süre kontrolü
  if (startDate && !isNaN(Date.parse(startDate)) && rentalDuration) {
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + parseInt(rentalDuration, 10));
    start.setDate(start.getDate() - 1); // 1 gün geri al
    setRentalDetails((prevState) => ({
      ...prevState,
      endDate: start.toISOString().slice(0, 10),
    }));
  } else {
    // Geçersiz veri varsa endDate'i sıfırla
    setRentalDetails((prevState) => ({
      ...prevState,
      endDate: "",
    }));
  }
};


// rentalDuration ve startDate değiştiğinde otomatik hesaplama yapılması için useEffect kullanımı
useEffect(() => {
  calculateRentalEndDate();
}, [rentalDetails.startDate, rentalDetails.rentalDuration]);

  const handleRentalSubmit = async () => {
    if (isSubmittingRental) return;

    setSubmitMessage('');
    setSubmitError('');
    if (!selectedCrane || !tenantDetails.companyName.trim() || !tenantDetails.workSiteName.trim()) {
      console.error('Eksik veya hatalı veri. Lütfen tüm alanları doldurun.');
      setSubmitError('Eksik veya hatalı veri var. Lütfen tüm zorunlu alanları doldurun.');
      return;
    }

    if (selectedCrane.hasOpenTechnicalService || technicalServiceCraneIds.has(Number(selectedCrane.id))) {
      setSubmitError('Bu kule vinç açık teknik servis sürecinde olduğu için kiraya verilemez.');
      return;
    }

    const installedHeight = getApiNumber(rentalDetails.installationHeight);
    const jibLength = getApiNumber(rentalDetails.JibLength);
    const rentalPeriod = getApiNumber(rentalDetails.rentalDuration);
    const rent = getApiNumber(rentalDetails.rentalFee);
    const invoiceInstallmentCount = getApiNumber(rentalDetails.invoiceInstallmentCount || rentalDetails.rentalDuration);
    const firstInvoiceDate = rentalDetails.firstInvoiceDate || rentalDetails.startDate;

    if (!installedHeight || !jibLength || !rentalPeriod || !rent || !rentalDetails.startDate || !rentalDetails.endDate) {
      setSubmitError('Kiralama icin yukseklik, bom, sure, kira bedeli, baslangic ve bitis tarihi zorunludur.');
      return;
    }

    setIsSubmittingRental(true);
    const formData = new FormData();
    appendValue(formData, 'TowerCraneId', selectedCrane.id);
    appendValue(formData, 'ManualTenantCompanyName', tenantDetails.companyName.trim());
    appendValue(formData, 'ManualTenantTaxNo', tenantDetails.taxNo.trim());
    appendValue(formData, 'ManualTenantAddress', tenantDetails.address.trim());
    appendValue(formData, 'ManualTenantAuthorizedPerson', tenantDetails.authorizedPerson.trim());
    appendValue(formData, 'ManualTenantPhone', tenantDetails.phone.trim());
    appendValue(formData, 'ManualTenantEmail', tenantDetails.email.trim());
    appendValue(formData, 'ManualWorkSiteName', tenantDetails.workSiteName.trim());
    appendValue(formData, 'Type', normalizeTowerCraneType(rentalDetails.chassisType || selectedCrane.type));
    formData.append(
      'HasWalkingGear',
      normalizeTowerCraneType(rentalDetails.chassisType || selectedCrane.type) === 'Walking' ||
        rentalDetails.hasWalkingGear === 'Var'
        ? 'true'
        : 'false'
    );
    appendNumber(formData, 'InstalledHeight', installedHeight);
    appendNumber(formData, 'JibLength', jibLength);
    appendNumber(formData, 'RentalPeriod', rentalPeriod);
    appendNumber(formData, 'Rent', rent);
    appendNumber(formData, 'InvoiceInstallmentCount', invoiceInstallmentCount || rentalPeriod);
    appendValue(formData, 'FirstInvoiceDate', firstInvoiceDate);
    appendValue(formData, 'RentalStartDate', rentalDetails.startDate);
    appendValue(formData, 'RentalEndDate', rentalDetails.endDate);
    // formData.append('Notes', JSON.stringify(rentalDetails.notes));

    (rentalDetails.lineItems || [])
      .filter(
        (item) =>
          String(item.description || '').trim() &&
          (getApiNumber(item.quantity) || 0) > 0 &&
          (getApiNumber(item.unitPrice) || 0) > 0
      )
      .forEach((item, index) => {
        const quantity = getApiNumber(item.quantity) || 0;
        const unitPrice = getApiNumber(item.unitPrice) || 0;
        formData.append(`LineItems[${index}].Description`, item.description.trim());
        formData.append(`LineItems[${index}].Quantity`, quantity);
        formData.append(`LineItems[${index}].UnitPrice`, unitPrice);
        formData.append(
          `LineItems[${index}].Amount`,
          quantity * unitPrice
        );
        formData.append(`LineItems[${index}].ItemType`, 'OneTime');
        formData.append(`LineItems[${index}].SortOrder`, index);
      });

    console.log(rentalDetails.notes);
    rentalDetails.notes.forEach((item, index) => { 
      formData.append(`Notes[${index}]`,item.note); 
    });
    files.forEach((item, index) => {
      formData.append(`Files[${index}].file`, item.file[0]); 
      formData.append(`Files[${index}].fileType`, item.fileType);  
    });


    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/Rental', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Kiralama başarıyla gerçekleşti:', response.data);
      setSubmitMessage('Vinç başarıyla kiraya gönderildi.');
      sessionStorage.removeItem('groupTrackingCacheV1');
      setTimeout(() => navigate('/grouptracking'), 600);

    } catch (error) {
      if (error.response) {
        const responseData = error.response?.data;
        const validationMessage = responseData?.errors
          ? Object.values(responseData.errors).flat().join(' ')
          : '';
        const apiMessage =
          responseData?.messages?.join(' ') ||
          validationMessage ||
          responseData?.message ||
          responseData?.title ||
          (typeof responseData === 'string' ? responseData : '') ||
          `Kiralama işlemi tamamlanamadı. HTTP ${error.response.status}`;
        console.error('Kiralama hatası:', responseData);
        setSubmitError(apiMessage);
      } else {
        console.error('Kiralama hatası:', error.message);
        setSubmitError(error.message || 'Kiralama işlemi sırasında bir hata oluştu.');
      }
    } finally {
      setIsSubmittingRental(false);
    }
  };
  const handleRezervSubmit = async () => {
    if (!selectedCrane) {
      console.error('Vinç seçilmeden rezerve işlemi yapılamaz.');
      return;
    }

    const installedHeight = getApiNumber(rentalDetails.installationHeight);
    const jibLength = getApiNumber(rentalDetails.JibLength);
    const rentalPeriod = getApiNumber(rentalDetails.rentalDuration);
    const rent = getApiNumber(rentalDetails.rentalFee);

    const formData = new FormData();
    appendValue(formData, 'TowerCraneId', selectedCrane.id);
    appendValue(formData, 'ManualTenantCompanyName', tenantDetails.companyName.trim());
    appendValue(formData, 'ManualTenantTaxNo', tenantDetails.taxNo.trim());
    appendValue(formData, 'ManualTenantAddress', tenantDetails.address.trim());
    appendValue(formData, 'ManualTenantAuthorizedPerson', tenantDetails.authorizedPerson.trim());
    appendValue(formData, 'ManualTenantPhone', tenantDetails.phone.trim());
    appendValue(formData, 'ManualTenantEmail', tenantDetails.email.trim());
    appendValue(formData, 'ManualWorkSiteName', tenantDetails.workSiteName.trim());
    appendNumber(formData, 'InstalledHeight', installedHeight || '');
    appendNumber(formData, 'JibLength', jibLength || '');
    appendValue(formData, 'Type', normalizeTowerCraneType(rentalDetails.chassisType || selectedCrane.type));
    formData.append(
      'HasWalkingGear',
      normalizeTowerCraneType(rentalDetails.chassisType || selectedCrane.type) === 'Walking' ||
        rentalDetails.hasWalkingGear === 'Var'
        ? 'true'
        : 'false'
    );
    appendNumber(formData, 'RentalPeriod', rentalPeriod || '');
    appendNumber(formData, 'Rent', rent || '');
    appendValue(formData, 'RentalStartDate', rentalDetails.startDate || '');
    appendValue(formData, 'RentalEndDate', rentalDetails.endDate || '');
    formData.append('HasExtraPeriod', false);
    formData.append('ExtraPeriod', 0);
  
    rentalDetails.notes.forEach((item, index) => { 
      formData.append(`Notes[${index}]`, item.note); 
    });
  
    files.forEach((item, index) => {
      formData.append(`Files[${index}].file`, item.file[0]); 
      formData.append(`Files[${index}].fileType`, item.fileType);  
    });
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}:`, pair[1]);
    }    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/Reserve', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
  
      console.log('Rezerve işlemi başarıyla gerçekleşti:', response.data);
      sessionStorage.removeItem('groupTrackingCacheV1');
      navigate('/grouptracking');
  
    } catch (error) {
      if (error.response) {
        console.error('Rezerve hatası:', error.response.data);
      } else {
        console.error('Rezerve hatası:', error.message);
      }
    }
  };
  
  return (
    <div className="crane-rental-page">
      <Navbar />
      <div className="rental-form">
        <h2>Kiraya Gönder</h2>
        {submitError && <div className="technical-service-alert error">{submitError}</div>}
        {submitMessage && <div className="technical-service-alert success">{submitMessage}</div>}
        {/* Kiraya veren firma */}
        <div className="form-row">
          <div className="form-group">
            <label>Makine Kiraya Veren Firma</label>
            <input type="text" value={STAR_CRANE_COMPANY_NAME} disabled />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Kule Vinç Seçin</label>
            <select value={selectedCrane?.id || ''} onChange={handleCraneChange} disabled={role === 'owner'}>
              <option value="">Boşta olan vinçlerden seçin</option>
              {availableCranes.map((crane) => (
                <option key={crane.id} value={crane.id}>
                  {crane.serialNo} - {crane.brand || '-'} {crane.model || ''}
                </option>
              ))}
            </select>
            {availableCranes.length === 0 && (
              <small className="technical-service-helper">Kiraya verilebilecek boşta vinç bulunamadı.</small>
            )}
          </div>
        </div>

        {selectedCrane && (
          <div className="crane-info">
            <div className="info-row">
              <span><strong>Seri No:</strong> {selectedCrane.serialNo}</span>
              <span><strong>Marka:</strong> {selectedCrane.brand}</span>
              <span><strong>Vinç Kodu:</strong> {selectedCrane.model}</span>
              <span><strong>Vinç Sahibi:</strong> {craneOwners.find((owner) => owner.id === selectedCrane.ownerId)?.name || '-'}</span>
            </div>
            <div className="info-row">
              <span><strong>Yükseklik:</strong> {selectedCrane.height}m</span>
              <span><strong>Bom:</strong> {selectedCrane.bom || '-'}m</span>
              <span><strong>Maksimum Tonaj:</strong> {selectedCrane.maxTonnage}</span>
              <span><strong>Tip:</strong> {getTowerCraneTypeLabel(selectedCrane.type)}</span>
            </div>
          </div>
        )}

        {selectedCrane && role !== 'owner' && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Kiracı Firma</label>
                <input
                  type="text"
                  name="companyName"
                  value={tenantDetails.companyName}
                  onChange={handleTenantDetailChange}
                  placeholder="Firma adını girin"
                />
              </div>
              <div className="form-group">
                <label>Şantiye</label>
                <input
                  type="text"
                  name="workSiteName"
                  value={tenantDetails.workSiteName}
                  onChange={handleTenantDetailChange}
                  placeholder="Şantiye adını girin"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vergi Dairesi</label>
                <input
                  type="text"
                  name="taxOffice"
                  value={tenantDetails.taxOffice}
                  onChange={handleTenantDetailChange}
                  placeholder="Vergi dairesi"
                />
              </div>
              <div className="form-group">
                <label>Vergi No / TCKN</label>
                <input
                  type="text"
                  name="taxNo"
                  value={tenantDetails.taxNo}
                  onChange={handleTenantDetailChange}
                  placeholder="Vergi numarası"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Adres</label>
                <input
                  type="text"
                  name="address"
                  value={tenantDetails.address}
                  onChange={handleTenantDetailChange}
                  placeholder="Firma / şantiye adresi"
                />
              </div>
              <div className="form-group">
                <label>Yetkili</label>
                <input
                  type="text"
                  name="authorizedPerson"
                  value={tenantDetails.authorizedPerson}
                  onChange={handleTenantDetailChange}
                  placeholder="Yetkili kişi"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="text"
                  name="phone"
                  value={tenantDetails.phone}
                  onChange={handleTenantDetailChange}
                  placeholder="Telefon"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={tenantDetails.email}
                  onChange={handleTenantDetailChange}
                  placeholder="Email"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kurulum Yüksekliği</label>
                <input type="number" name="installationHeight" value={rentalDetails.installationHeight} readOnly />
              </div>
              <div className="form-group">
                <label>Bom</label>
                <input type="number" name="JibLength" value={rentalDetails.JibLength} readOnly />
              </div>
              <div className="form-group">
                <label>Kira Süresi (Ay)</label>
                <input type="number" name="rentalDuration" value={rentalDetails.rentalDuration} onChange={handleRentalDetailChange} />
              </div>
            </div>

            <div className="form-row">
            <div className="form-group">
              <label>Kira Bedeli</label>
              <input type="number" name="rentalFee" value={rentalDetails.rentalFee} onChange={handleRentalDetailChange} />
            </div>
              <div className="form-group">
                <label>Fatura Taksit Sayısı</label>
                <input
                  type="number"
                  min="1"
                  name="invoiceInstallmentCount"
                  value={rentalDetails.invoiceInstallmentCount}
                  onChange={handleRentalDetailChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kira Başlangıç Tarihi</label>
                <input type="date" name="startDate" value={rentalDetails.startDate} onChange={handleRentalDetailChange} />
              </div>
              <div className="form-group">
                <label>Kira Bitiş Tarihi</label>
                <input type="date" name="endDate" value={rentalDetails.endDate} onChange={handleRentalDetailChange} disabled />
                </div>
              <div className="form-group">
                <label>İlk Fatura Tarihi</label>
                <input
                  type="date"
                  name="firstInvoiceDate"
                  value={rentalDetails.firstInvoiceDate}
                  onChange={handleRentalDetailChange}
                />
              </div>
            </div>

            <div className="form-row notes-container">
              <label>İlk Faturaya Eklenecek Kalemler</label>
              <div className="form-group note-group">
                <strong>Hizmet Kalem Adı</strong>
                <strong>Adet</strong>
                <strong>Birim Fiyat</strong>
                <strong>Tutar</strong>
                <span />
              </div>
              {(rentalDetails.lineItems || []).map((item) => (
                <div key={item.localId} className="form-group note-group">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(item.localId, 'description', e.target.value)}
                    placeholder="Hizmet / Kalem Adı"
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(item.localId, 'quantity', e.target.value)}
                    placeholder="Adet"
                  />
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => handleLineItemChange(item.localId, 'unitPrice', e.target.value)}
                    placeholder="Birim Fiyat"
                  />
                  <input
                    type="text"
                    value={(
                      Number(item.quantity || 0) * Number(item.unitPrice || 0)
                    ).toLocaleString('tr-TR')}
                    placeholder="Tutar"
                    disabled
                  />
                  <button
                    type="button"
                    className="add-note-btn"
                    onClick={() => removeLineItem(item.localId)}
                    disabled={(rentalDetails.lineItems || []).length === 1}
                  >
                    Sil
                  </button>
                </div>
              ))}
              <button type="button" onClick={addLineItem} className="add-note-btn">
                + Kalem Ekle
              </button>
            </div>

            {/* Dosya Yükleme Alanları */}
            <div className="form-row">
              <div className="form-group">
                <label>Teklif Dosyası</label>
                <input type="file" multiple onChange={(e) => handleFileChange(e, 'Offer')} />
              </div>
              <div className="form-group">
                <label>Sözleşme Dosyası</label>
                <input type="file" multiple onChange={(e) => handleFileChange(e, 'Contract')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Servis Formu</label>
                <input type="file" multiple onChange={(e) => handleFileChange(e, 'ServiceForm')} />
              </div>
              <div className="form-group">
                <label>Görseller</label>
                <input type="file" accept=".jpg, .jpeg, .png, .gif, .webp" multiple onChange={(e) => handleFileChange(e, 'Image')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ödeme Dekontu</label>
                <input type="file" multiple onChange={(e) => handleFileChange(e, 'paymentDoc')} />
              </div>
              <div className="form-group">
                <label>Çek Görselleri</label>
                <input type="file" multiple onChange={(e) => handleFileChange(e, 'checkImages')} />
              </div>
            </div>

            {/* Notlar dinamik olarak ekleniyor */}
            <div className="form-row notes-container">
              {rentalDetails.notes.map((note, index) => (
                <div key={index} className="form-group note-group">
                <label>Notlar</label>
                  
                  <input
                    type="text"
                    name={`note-${index}`}
                    value={note.note}
                    onChange={(e) => handleNoteChange(index, e.target.value)}
                    placeholder={`Not ${index + 1}`}
                  />
                </div>
              ))}
              <button type="button" onClick={addNote} className="add-note-btn">+ Not Ekle</button>
            </div>

              <div className='rental-btn-div'>

            <button className="submit-btn rezerv-btn" onClick={handleRezervSubmit}>Rezerve</button>
            <button className="submit-btn rental-btn" onClick={handleRentalSubmit} disabled={isSubmittingRental}>{isSubmittingRental ? 'Gönderiliyor...' : 'Kiraya Gönder'}</button>
              </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CraneRental;

